import { Reveal } from "@components/ui";
import arrowRight from "@assets/images/vuesax-linear-arrow-right.svg";
import leakImage from "@assets/images/Frame-1618869673@2x.png";

const leakSigns = [
  {
    sign: "Strong smell similar to rotten eggs",
    detail: "odorant added for detection",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10z" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    sign: "Hissing sound near pipes, valves, or meters",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M12 3v18M8 8l-4 4 4 4M16 8l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    sign: "Sudden dead plants near gas lines",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M7 20h10M12 20v-6m0 0c3-1 5-3 5-7-3 1-5 3-5 7zm0 0c-3-1-5-3-5-7 3 1 5 3 5 7z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    sign: "Weak stove flame or incomplete burning",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M12 22c3.5 0 6-2.5 6-6 0-4-3-7-6-10-3 3-6 6-6 10 0 3.5 2.5 6 6 6z" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    sign: "Headache, dizziness, or nausea indoors",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
        <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10zM8 15h8M9 9h.01M15 9h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

const actionFlow = [
  { step: "If any sign appears", bold: false },
  { step: "Shut valve", bold: true },
  { step: "Evacuate", bold: true },
  { step: "Call GASIS immediately", bold: true },
];

export function GasLeakRecognitionSection() {
  return (
    <section className="w-full bg-white overflow-hidden px-5 md:px-10 lg:px-[180px] py-12 md:py-16 lg:py-[100px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col lg:flex-row items-center gap-10 md:gap-14 lg:gap-20">
        {/* Text Content */}
        <Reveal direction="left" className="w-full lg:flex-1">
          <div className="flex flex-col items-start gap-8 md:gap-10">
            <div className="flex flex-col items-start gap-5 md:gap-7">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
                Detection
              </span>
              <h2 className="text-[28px] md:text-[44px] lg:text-[54px] font-semibold tracking-[-0.04em] text-[#122d63] leading-[1.1]">
                How to Recognize a Gas Leak
              </h2>

              <div className="flex flex-col gap-3 md:gap-4 w-full">
                {leakSigns.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3.5 p-3.5 md:p-4 rounded-[14px] bg-[#f8f9fb] hover:bg-white hover:shadow-[0_2px_16px_rgba(0,0,0,0.07)] border border-transparent hover:border-[#e8e9ed] transition-all duration-300"
                  >
                    <div className="w-9 h-9 md:w-10 md:h-10 rounded-[10px] bg-[#f24d2b]/10 text-[#f24d2b] flex items-center justify-center shrink-0">
                      {item.icon}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <p className="text-[14px] md:text-[17px] lg:text-[18px] tracking-[-0.03em] text-[#1e1f24] leading-[1.4]">
                        {item.sign}
                      </p>
                      {item.detail && (
                        <span className="text-[11px] md:text-[12px] text-[#62636c]/70 italic">
                          {item.detail}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Flow Bar */}
            <div className="w-full rounded-[14px] bg-gradient-to-r from-[#fff5f3] to-[#fff0ec] border border-[#f24d2b]/30 px-4 md:px-6 py-4 md:py-5">
              <div className="flex flex-wrap items-center gap-2 md:gap-3">
                {actionFlow.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 md:gap-3">
                    <span
                      className={`text-[13px] md:text-[16px] lg:text-[18px] tracking-[-0.03em] whitespace-nowrap ${
                        item.bold
                          ? "font-semibold text-[#f24d2b]"
                          : "text-[#1e1f24]"
                      }`}
                    >
                      {item.step}
                    </span>
                    {index < actionFlow.length - 1 && (
                      <img
                        src={arrowRight}
                        alt=""
                        className="w-4 h-4 md:w-5 md:h-5 shrink-0 opacity-60"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Image */}
        <Reveal direction="right" className="w-full lg:w-[500px] shrink-0">
          <img
            src={leakImage}
            alt="Gas leak recognition"
            className="w-full h-auto rounded-[20px] object-cover"
          />
        </Reveal>
      </div>
    </section>
  );
}
